import React, { useRef, useEffect } from "react";
import {
  Group,
  Mesh,
  MeshLambertMaterial,
  DoubleSide,
  SphereGeometry,
  CircleGeometry,
  CylinderGeometry,
} from "three";
import { useThree, extend, ReactThreeFiber } from "react-three-fiber";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

extend({ OrbitControls });

declare global {
  namespace JSX {
    interface IntrinsicElements {
      orbitControls: ReactThreeFiber.Node<OrbitControls, typeof OrbitControls>;
    }
  }
}

const Pokeball: React.FC = () => {
  const pokeballRef = useRef<Group>(null);
  const { camera, gl, scene } = useThree();

  useEffect(() => {
    if (!pokeballRef.current) return;

    const pokeball_size = 23;
    const pokeball_segments = 48;

    const pokeball = new Group();
    pokeball.name = "Pokeball";

    // Upper side
    const ballUpGeom = new Mesh(
      new SphereGeometry(
        pokeball_size,
        pokeball_segments,
        pokeball_segments,
        0,
        Math.PI * 2,
        0,
        (Math.PI / 2) * 0.97
      ),
      new MeshLambertMaterial({ color: 0xff0000, side: DoubleSide })
    );
    ballUpGeom.name = "Pokeball upper side";

    // Closing
    const ballUpClosingGeom = new Mesh(
      new CircleGeometry(pokeball_size, pokeball_segments),
      new MeshLambertMaterial({ color: 0xff0000, side: DoubleSide })
    );
    ballUpClosingGeom.rotateX(Math.PI / 2);
    ballUpClosingGeom.position.set(0, pokeball_size - pokeball_size * 0.95, 0);
    ballUpClosingGeom.name = "Pokeball upper closing";

    // Lower side
    const ballDownGeom = new Mesh(
      new SphereGeometry(
        pokeball_size,
        pokeball_segments,
        pokeball_segments,
        0,
        Math.PI * 2,
        (Math.PI / 2) * 1.03,
        Math.PI / 2
      ),
      new MeshLambertMaterial({ color: "0xffffff", side: DoubleSide })
    );
    ballDownGeom.name = "Pokeball Lower side";

    // Closing
    const ballDownClosingGeom = new Mesh(
      new CircleGeometry(pokeball_size, pokeball_segments),
      new MeshLambertMaterial({ color: "0xffffff", side: DoubleSide })
    );
    ballDownClosingGeom.rotateX(Math.PI / 2);
    ballDownClosingGeom.position.set(
      0,
      -(pokeball_size - pokeball_size * 0.95),
      0
    );
    ballDownClosingGeom.name = "Pokeball lower closing";

    // Inner side
    const ballInnerGeom = new Mesh(
      new SphereGeometry(
        pokeball_size * 0.95,
        pokeball_segments,
        pokeball_segments
      ),
      new MeshLambertMaterial({ color: 0x000000, side: DoubleSide })
    );
    ballInnerGeom.name = "Pokeball inner side";

    // Opening
    const opening = new Group();
    opening.name = "Opening";

    // Outer
    const openingOuterGeom = new Mesh(
      new CylinderGeometry(5, 5, 3, pokeball_segments),
      new MeshLambertMaterial({ color: 0x000000 })
    );
    openingOuterGeom.name = "Outer";

    // Middle
    const openingMiddleGeom = new Mesh(
      new CylinderGeometry(3, 3, 3, pokeball_segments),
      new MeshLambertMaterial({ color: "0xffffff" })
    );
    openingMiddleGeom.name = "Middle";
    openingMiddleGeom.position.y = 0.7;

    // Inner
    const openingInnerGeom = new Mesh(
      new CylinderGeometry(2, 2, 3, pokeball_segments),
      new MeshLambertMaterial({ color: "0xffffff" })
    );
    openingInnerGeom.name = "Inner";
    openingInnerGeom.position.y = 1;

    opening.rotateX(Math.PI / 2);
    opening.position.set(0, 0, pokeball_size * 0.93);

    opening.add(openingOuterGeom);
    opening.add(openingMiddleGeom);
    opening.add(openingInnerGeom);

    // Putting all together
    pokeball.add(ballUpGeom);
    pokeball.add(ballUpClosingGeom);
    pokeball.add(ballDownGeom);
    pokeball.add(ballDownClosingGeom);
    pokeball.add(ballInnerGeom);
    pokeball.add(opening);

    pokeballRef.current.add(pokeball);

    // Setup OrbitControls
    const controls = new OrbitControls(camera, gl.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.enableZoom = false;
    controls.target.set(0, 0, 0);
    controls.update();

    camera.position.set(0, 0, 40);
    camera.lookAt(0, 0, 0);

    return () => {
      controls.dispose();
    };
  }, [camera, gl.domElement]);

  return (
    <>
      <group ref={pokeballRef} />
      <orbitControls args={[camera, gl.domElement]} />
    </>
  );
};

export default Pokeball;
